import { useSelector } from 'react-redux'
import type { Constant } from '@pbt/pbt-ui-components'
import { LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import {
  ConsentStatusName,
  PriorVetCareStatusName,
} from '~/constants/previousMedicalRecord'
import i18nPortal from '~/locales/i18n'
import { getConsentStatus } from '~/store/reducers/constants'

// --- Consent texts hook ---

export interface ConsentTexts {
  consentText: string | null
  descriptionText: string
  showConsentLine: boolean
}

/**
 * Resolves consent text based on consent entity status.
 */
export function resolveConsentText(
  consentEntity: Constant | undefined,
): string | null {
  if (!consentEntity) {
    return null
  }
  if (consentEntity.name === ConsentStatusName.GRANTED) {
    return i18nPortal.t('Common:PMR_CONSENT.CONSENT_YES')
  }
  if (consentEntity.name === ConsentStatusName.DENIED) {
    return i18nPortal.t('Common:PMR_CONSENT.CONSENT_NO')
  }

  return LanguageUtils.getTranslatedFieldName(consentEntity)
}

export function getDescriptionText({
  consentEntity,
  nullDescription,
}: {
  consentEntity: Constant | undefined
  nullDescription: string
}): string {
  if (consentEntity?.name === ConsentStatusName.GRANTED) {
    return i18nPortal.t('Common:PMR_CONSENT.CONSENT_YES_DESCRIPTION')
  }

  if (consentEntity?.name === ConsentStatusName.DENIED) {
    return i18nPortal.t('Common:PMR_CONSENT.CONSENT_NO_DESCRIPTION')
  }

  return nullDescription
}

/**
 * Resolves consent display texts from consentStatus hashId using ConsentStatus constants from Redux.
 * Used by useConsentTexts and by tests (getConsentTexts).
 */
export function getConsentTexts(
  consentStatusId: string | undefined,
  nullDescription: string,
  consentStatusConstants: Constant[],
): ConsentTexts {
  const consentEntity = Utils.findById(consentStatusId, consentStatusConstants)
  const showConsentLine = consentStatusId != null
  const consentText = resolveConsentText(consentEntity)
  const descriptionText = getDescriptionText({ consentEntity, nullDescription })

  return {
    consentText,
    descriptionText,
    showConsentLine,
  }
}

/**
 * Hook: resolves consent display texts from consentStatus hashId.
 * Uses useSelector(getConsentStatus) internally.
 */
export function useConsentTexts(
  consentStatusId: string | undefined,
  nullDescription: string,
): ConsentTexts {
  const consentStatusConstants = useSelector(getConsentStatus)
  return getConsentTexts(
    consentStatusId,
    nullDescription,
    consentStatusConstants,
  )
}

// --- Manual contact id (UUID v4) ---

/**
 * RFC 4122 UUID v4 pattern (e.g. crypto.randomUUID()).
 * Manual-added contacts use this id; we omit id when sending to API.
 */
export const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isManualContactId(id: string | undefined): boolean {
  return id != null && UUID_V4_REGEX.test(id)
}

// --- Simple message resolver ---

/**
 * Determines the simple message to display for previous medical records based on status.
 * Returns a translated message or null if the full consent/clinics view should be shown.
 * Uses i18nPortal so behavior is consistent regardless of caller's useTranslation namespace.
 *
 * @param priorVetCareStatus - Prior vet care status name (YES, NO, UNKNOWN)
 * @returns Translated message string or null
 */
export function getSimplePmrMessage(
  priorVetCareStatus: string | undefined,
): string | null {
  if (priorVetCareStatus === PriorVetCareStatusName.NO) {
    return i18nPortal.t('Common:PMR_CONSENT.PATIENT_NOT_BEEN_TO_VET')
  }

  if (priorVetCareStatus === PriorVetCareStatusName.UNKNOWN) {
    return i18nPortal.t('Common:PMR_CONSENT.CONSENT_UNSURE_DESCRIPTION')
  }

  return null
}
